import React, { useMemo, useEffect, useState } from 'react';
import { SlideProps } from '../../types';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { useQuery } from 'react-query';
import { getRankedControls } from 'src/api/controllibraries';
import { useAssessmentScenariosWizardQuery, useControlsRmpQuery } from 'src/graphql/_generated';
import { TableIds } from 'src/api/types/misc';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty } from 'src/components/GenericTable';
import { useColumns } from './useColumns';
import { ControlWithScenarios } from './types';
import { AiSvg } from 'src/assets/dynamicSvg';
import { ControlTableExpandContent } from './comps';
import { useFormikContext } from 'formik';
import { WizardRecomendedControlsFormValues } from 'src/api/types/wizard';
import { getControlId } from '../../util';
import _ from 'lodash';
import { useAuth } from 'src/state/auth';
import { QKeys } from 'src/api/types';
import { SuggestModifiersChecker } from 'src/components/modals/comps';

export const LinkSlide: React.FC<SlideProps> = ({ assessmentId }) => {
  const { values, setFieldValue, resetForm } = useFormikContext<WizardRecomendedControlsFormValues>();
  const [isDisabledModSuggest, setIsDisabledModSuggest] = useState(false);
  const { data: { assessment } = {}, loading } = useAssessmentScenariosWizardQuery({
    variables: { id: assessmentId },
    fetchPolicy: 'network-only',
  });
  const isAssessmentLoading = loading && !assessment;
  const { workspaceId } = useAuth();

  const { data: { controls: rmpControls = [] } = {}, loading: loadingRmp } = useControlsRmpQuery({
    variables: { workspaceId: workspaceId },
  });
  const isRmpControlsLoading = loadingRmp && !rmpControls.length;

  const scenarios = assessment?.scenarios || [];
  const scenarioIds = scenarios.map((el) => el.id);

  const { data: controlsRaw = [], isLoading: isControlsLoading } = useQuery(
    [QKeys.RankedControlsAssessment, scenarioIds],
    async () => Promise.all(scenarioIds.map((el) => getRankedControls(el))),
  );

  const isLoading = isControlsLoading || isAssessmentLoading || isRmpControlsLoading;

  const controlsWithScenarios: ControlWithScenarios[] = useMemo(() => {
    if (_.isEmpty(controlsRaw) || _.isEmpty(scenarios)) return [];

    const scenarioMap = new Map(scenarios.map((scenario) => [scenario.id, scenario]));

    const controlData = _(controlsRaw)
      .flatMap((scenarioControls, scenarioIndex) =>
        _(scenarioControls)
          // .filter((control) => control.ux_id)
          .map((control) => {
            return {
              uniqueId: getControlId(control),
              control,
              scenarioId: scenarioIds[scenarioIndex],
              score: control.final_score,
            };
          })
          .value(),
      )
      .groupBy('uniqueId')
      .mapValues((controlGroup) => {
        return {
          control: controlGroup[0].control,
          scenarios: _(controlGroup)
            .map(({ scenarioId, score, control }) => {
              const scenario = scenarioMap.get(scenarioId);

              const isLinked = !!scenario?.riskManagementPlans.some((rmp) => {
                const frameworkControls = rmp.control?.frameworkControls || [];

                return frameworkControls.some((el) => {
                  const rmpFrameworkLibraryId = el?.control_identifier;

                  const match =
                    rmpFrameworkLibraryId === (control as any).controlId ||
                    rmpFrameworkLibraryId === control.controlLibraryId;

                  return match;
                });
              });

              return {
                scenario,
                score,
                isLinked,
              };
            })
            .filter(
              (item): item is { scenario: NonNullable<typeof item.scenario>; score: number; isLinked: boolean } =>
                item.scenario !== undefined,
            )
            .value(),
        };
      })
      .values()
      .value();

    return controlData;
  }, [controlsRaw, scenarioIds, scenarios, rmpControls]);

  const { minScore, maxScore } = useMemo(() => {
    if (_.isEmpty(controlsWithScenarios)) return { minScore: 0, maxScore: 500 };

    const allScores = _(controlsWithScenarios)
      .flatMap((control) => _.map(control.scenarios, 'score'))
      .compact()
      .value();

    return {
      minScore: _.min(allScores) ?? 0,
      maxScore: _.max(allScores) ?? 500,
    };
  }, [controlsWithScenarios]);

  useEffect(() => {
    if (_.isEmpty(controlsWithScenarios) || !_.isEmpty(values.selectedControls)) return;

    const initialSelectedControls: Record<string, Record<string, boolean>> = {};

    controlsWithScenarios.forEach((controlWithScenarios) => {
      const controlId = getControlId(controlWithScenarios.control);
      const availableScenarios = controlWithScenarios.scenarios.filter((s) => !s.isLinked);

      if (availableScenarios.length > 0) {
        initialSelectedControls[controlId] = {};
        availableScenarios.forEach((scenarioData) => {
          initialSelectedControls[controlId][scenarioData.scenario.id] = true;
        });
      }
    });

    if (!_.isEmpty(initialSelectedControls)) {
      resetForm({
        values: {
          ...values,
          selectedControls: initialSelectedControls,
        },
      });
    }
  }, [controlsWithScenarios, values.selectedControls, resetForm, values]);

  const { columns, GTColumnSelectAnchorExported } = useColumns({
    data: controlsWithScenarios,
    selectedControls: values.selectedControls,
    setFieldValue,
    rmpControls,
  });

  useEffect(() => {
    const hasAnySelection = Object.values(values.selectedControls || {}).some((controlScenarios) =>
      Object.values(controlScenarios).some((isSelected) => isSelected === true),
    );

    setIsDisabledModSuggest(!hasAnySelection);

    if (!hasAnySelection) {
      setFieldValue('addRecommendedModifiers', false);
    }
  }, [values.selectedControls, setFieldValue, values.addRecommendedModifiers]);

  return (
    <div>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          <AiSvg style={{ marginRight: '8px' }} />
          <Poppins px={14} color="cflowerBlue">
            The following controls are recommended for risk scenarios in this assessment. You can remove any controls or
            unlink them from specific risk scenarios
          </Poppins>
          <Spacer $px={15} />
          <GenericTable
            tableId={TableIds.wizardRecomendedControls}
            data={controlsWithScenarios}
            columns={columns}
            GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
            searchable
            rowDisabled={(row) => _.every(row.scenarios, 'isLinked')}
            expandContent={(data) => (
              <ControlTableExpandContent
                data={data.scenarios}
                assessment={{
                  id: assessmentId,
                  name: assessment?.name!,
                }}
                minScore={minScore}
                maxScore={maxScore}
                selectedControls={values.selectedControls}
                setFieldValue={setFieldValue}
                controlId={getControlId(data.control)}
              />
            )}
            hideExpandContentWhenClosed
            itemHeight={50}
            initialSort={[
              { id: 'frameworkShortname', desc: false },
              { id: 'control_identifier', desc: false },
            ]}
            emptyRow={
              <GTRowEmpty
                itemHeight={50}
                text={genEmtyRowTextComponent({
                  startText: 'No suggestions found',
                  endText: '',
                })}
              />
            }
          />
          {
            <SuggestModifiersChecker
              isDisabledModSuggest={isDisabledModSuggest}
              addRecommendedModifiers={values.addRecommendedModifiers}
              setAddRecommendedModifiers={(val) => setFieldValue('addRecommendedModifiers', val)}
            />
          }
          <Spacer $px={10} />
        </>
      )}
    </div>
  );
};
